import clsx from 'clsx';
import { Checkbox } from 'common/checkbox-v2';
import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { RadioButton } from 'common/radio-button';
import { IFilter, IRegionItem } from 'constants/types';
import { FormikValues } from 'formik';
import Image from 'next/image';
import { useCallback, useMemo, useState } from 'react';
import { Dropdown } from '../dropdown';
import { DropdownItem } from '../dropdown-item';
import { CommonItem } from './common-item';
import { SearchInput } from './search-input';
import styles from './styles.module.css';
import { RatingItem } from './rating-item';
import { RTPItem } from './rtp-item';

interface FilterProps {
  item: string;
  icon?: string;
  className?: string;
}

export const FilterItem = ({ item, icon, className }: FilterProps) => {
  return (
    <div className={clsx(styles.item, className)}>
      {icon && <Image src={icon} alt={`icon`} width={20} height={12} />}
      <div className={styles.text}>{item}</div>
      <Icon.ArrowDown />
    </div>
  );
};

interface FiltersProps {
  isSingle?: boolean;
  filter: IFilter;
  values: FormikValues;
  defaultValues?: any;
  setFieldValue: (field: string, value: any) => void;
  isSubmitting: boolean;
  dirty: boolean;
  onSubmit: () => void;
  filterItemClassname?: string;
}

export const Filters = ({
  filter,
  values,
  setFieldValue,
  isSubmitting,
  dirty,
  defaultValues,
  onSubmit,
  filterItemClassname,
}: FiltersProps) => {
  const [isDropdown, setIsDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedItems = useMemo(
    () => values[filter.key] || [],
    [filter.key, values]
  );

  const handleFilterClick = useCallback(
    (itemValue: string, isSingle?: boolean) => {
      let updatedSelection = selectedItems.includes(itemValue)
        ? (Array.isArray(selectedItems) ? selectedItems : [])?.filter(
            (item: any) => item !== itemValue
          )
        : [...selectedItems, itemValue];

      if (isSingle) {
        updatedSelection = [itemValue];
      }

      const result = isSingle ? updatedSelection[0] : updatedSelection;

      setFieldValue(filter.key, result);

      if (isSingle) {
        onSubmit();
        setIsDropdown(false);
      }
    },
    [selectedItems, setFieldValue, filter.key, onSubmit]
  );

  const clearFilter = useCallback(() => {
    setFieldValue(filter.key, []);
  }, [setFieldValue, filter]);

  const renderFilterItems = useMemo(() => {
    if (!Array.isArray(filter.items)) {
      return <div className={styles.filter}>{'No items available'}</div>;
    }

    const filteredItems = filter.items.filter((item) => {
      const itemLabel = typeof item === 'string' ? item : item.name || '';
      return itemLabel.toLowerCase().includes(searchQuery.toLowerCase());
    });

    return (
      <>
        {filter.type === 'countries' && (
          <>
            <DropdownItem key={'All countries'}>
              <div
                onClick={() => handleFilterClick('all', filter.isSingle)}
                className={styles.filter}
              >
                <CommonItem label={'All countries'} />
                <RadioButton
                  checked={
                    selectedItems?.length
                      ? selectedItems === 'all'
                      : !defaultValues[filter.key]?.length
                  }
                />
              </div>
            </DropdownItem>
            {filteredItems.length !== 0 && <div className={styles.divider} />}
          </>
        )}

        {filteredItems.map((item, index) => {
          const itemValue = typeof item === 'string' ? item : item._id || '';
          const itemLabel =
            typeof item === 'string' ? item : item.name || 'Unknown';

          let isSelected = false;

          if (
            selectedItems.includes(itemValue) ||
            selectedItems === itemValue
          ) {
            isSelected = true;
          }

          if (!selectedItems?.length) {
            if (
              defaultValues?.[filter.key]?.includes(itemValue) ||
              defaultValues?.[filter.key] === itemValue
            ) {
              isSelected = true;
            }
          }

          return (
            <DropdownItem key={itemValue}>
              <div
                onClick={() => handleFilterClick(itemValue, filter.isSingle)}
                className={styles.filter}
              >
                {(() => {
                  switch (filter.type) {
                    case 'countries':
                      return (
                        <>
                          <CommonItem
                            label={itemLabel}
                            icon={(item as IRegionItem).imageUrl}
                          />
                          <RadioButton checked={isSelected} />
                        </>
                      );
                    case 'rating':
                      return (
                        <>
                          <RatingItem data={item as IRegionItem} />
                          <Checkbox checked={isSelected} />
                        </>
                      );
                    case 'rtp':
                      return (
                        <>
                          <RTPItem data={item as IRegionItem} />
                          <Checkbox checked={isSelected} />
                        </>
                      );
                  }

                  if (filter.isSingle) {
                    return (
                      <>
                        <CommonItem label={itemLabel} />
                        <RadioButton checked={isSelected} />
                      </>
                    );
                  } else {
                    return (
                      <>
                        <CommonItem label={itemLabel} />
                        <Checkbox disabled checked={isSelected} />
                      </>
                    );
                  }
                })()}
              </div>
              {index !== filteredItems.length - 1 && (
                <div className={styles.divider} />
              )}
            </DropdownItem>
          );
        })}
      </>
    );
  }, [
    defaultValues,
    filter.isSingle,
    filter.items,
    filter.key,
    filter.type,
    handleFilterClick,
    searchQuery,
    selectedItems,
  ]);

  return (
    <div>
      <Dropdown
        isDropdown={isDropdown}
        setIsDropdown={setIsDropdown}
        button={
          <FilterItem
            className={filterItemClassname}
            item={filter.text}
            icon={filter?.icon}
          />
        }
        setSearchQuery={setSearchQuery}
      >
        {(() => {
          switch (filter.type) {
            case 'countries':
              return (
                <>
                  <div className={clsx(styles.header, styles.header_avg_size)}>
                    <h2 className={clsx(styles.title, styles.text_align_start)}>
                      {filter.title}
                    </h2>
                    <div
                      className={styles.iconWrapper}
                      onClick={() => setIsDropdown(false)}
                    >
                      <Icon.CloseSecond width={14} height={14} />
                    </div>
                  </div>
                  <SearchInput onSearchChange={setSearchQuery} />
                </>
              );
            default:
              return (
                <div className={styles.header}>
                  <h2 className={styles.title}>{filter.title}</h2>
                  <div
                    className={styles.iconWrapper}
                    onClick={() => setIsDropdown(false)}
                  >
                    <Icon.CloseSecond width={14} height={14} />
                  </div>
                </div>
              );
          }
        })()}
        <div className={styles.filterItemsContainer}>{renderFilterItems}</div>
        {!filter.isSingle && (
          <footer className={styles.footer}>
            <div className={styles.footerContent}>
              <Button
                text={'Apply'}
                onClick={() => {
                  setIsDropdown(false);
                  onSubmit();
                }}
                className={styles.applyButton}
                disabled={!dirty || isSubmitting}
              />
              <Button
                className={styles.clearFilter}
                onClick={() => {
                  setIsDropdown(false);
                  clearFilter();
                  onSubmit();
                }}
                type="button"
                text={'Clear Filter'}
                icon={<Icon.Close fill="#caf413" width={14} height={14} />}
              />
            </div>
          </footer>
        )}
      </Dropdown>

      {/* {filter.isSingle &&
        selectedItems?.length > 0 &&
        filter.key !== 'sortBy' && (
          <div className={styles.clearFilterContainer}>
            <Button
              className={styles.clearFilter}
              onClick={() => {
                setIsDropdown(false);
                clearFilter();
                onSubmit();
              }}
              type="button"
              text={t('Clear Filter')}
              icon={<Icon.Close fill="#caf413" width={16} height={16} />}
            />
          </div>
        )} */}
    </div>
  );
};
